import type { MetaDescriptor, MetaFunction } from "@remix-run/node";

/**
 * Helper to merge parent meta tags with route-specific meta tags.
 * This ensures viewport and other important meta tags are inherited from root.
 *
 * AIDEV-NOTE: Remix doesn't automatically merge meta tags - routes override parent meta
 *
 * @param metaFn - Function that returns route-specific meta tags
 * @returns MetaFunction that merges parent and route meta tags
 */
export const mergeMeta =
    (
        metaFn: (args: Parameters<MetaFunction>[0]) => MetaDescriptor[],
    ): MetaFunction =>
    (args) => {
        const routeMeta = metaFn(args);

        // AIDEV-NOTE: Deduplicate parent meta tags to prevent duplicates in nested routes
        // Since nested routes inherit from all ancestors, we need to filter out duplicates
        const seenMetaTags = new Set<string>();
        const parentMeta = args.matches
            .flatMap((match) => match.meta ?? [])
            .filter((meta) => {
                // Filter out meta tags that the route is explicitly overriding
                if ("title" in meta) {
                    return !routeMeta.some((m) => "title" in m);
                }
                if ("name" in meta && meta.name) {
                    // Check if route is overriding this tag
                    if (
                        routeMeta.some(
                            (m) => "name" in m && m.name === meta.name,
                        )
                    ) {
                        return false;
                    }
                    // Check if we've already seen this name tag (deduplicate)
                    const key = `name:${meta.name}`;
                    if (seenMetaTags.has(key)) {
                        return false;
                    }
                    seenMetaTags.add(key);
                    return true;
                }
                if ("property" in meta && meta.property) {
                    // Check if route is overriding this tag
                    if (
                        routeMeta.some(
                            (m) =>
                                "property" in m && m.property === meta.property,
                        )
                    ) {
                        return false;
                    }
                    // Check if we've already seen this property tag (deduplicate)
                    const key = `property:${meta.property}`;
                    if (seenMetaTags.has(key)) {
                        return false;
                    }
                    seenMetaTags.add(key);
                    return true;
                }
                return true;
            });

        // Route meta takes precedence over parent meta
        return [...parentMeta, ...routeMeta];
    };

/**
 * Creates a MetaFunction that only sets the title while preserving all parent meta tags
 */
export const titleMeta = (title: string): MetaFunction =>
    mergeMeta(() => [{ title }]);

/**
 * Creates a MetaFunction that sets title and description while preserving other parent meta tags
 */
export const pageMeta = (title: string, description: string): MetaFunction =>
    mergeMeta(() => [{ title }, { name: "description", content: description }]);
